import React from "react";
import { ArrowRight } from "lucide-react";
import type { IssueReport, IssueStatus } from "../../types/issue-triage";
import { moveTargets } from "../../lib/issueTriage/triageMoves";

interface MoveSelectProps {
  issue: Pick<IssueReport, "status">;
  onMove: (status: IssueStatus) => void;
  /** Small inline variant for Kanban cards. */
  compact?: boolean;
}

/**
 * "Move to..." for one issue: every stage except the one it is in (L-1224). Stops click/pointer
 * propagation so using it on a card neither opens the issue nor starts a drag. It always shows the
 * prompt, never a chosen stage, so the same stage can be picked again after a refused move.
 */
export function MoveSelect({ issue, onMove, compact }: MoveSelectProps) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <label
      className={`inline-flex items-center gap-1 ${compact ? "text-xs" : "text-sm"} text-slate-600`}
      onClick={stop}
      onPointerDown={stop}
      onKeyDown={stop}
    >
      <ArrowRight className={compact ? "w-3 h-3" : "w-4 h-4"} aria-hidden="true" />
      <select
        aria-label="Move to"
        value=""
        onChange={(e) => {
          if (e.target.value) onMove(e.target.value as IssueStatus);
        }}
        className={`rounded-md ring-1 ring-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#3FA5FF] ${
          compact ? "px-1 py-0.5 text-xs max-w-[110px]" : "px-2 py-1 text-sm"
        }`}
      >
        <option value="">Move to...</option>
        {moveTargets(issue).map((t) => (
          <option key={t.status} value={t.status}>
            {t.label}
          </option>
        ))}
      </select>
    </label>
  );
}
